import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, App, Steps, Image, Typography, Spin } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { authService } from '@/auth/services/auth-service';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth-store';
import type { LoginCredentials } from '../types';

const { Step } = Steps;
const { Title, Paragraph } = Typography;

export const LoginPage: React.FC = () => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0); // 0: Login, 1: Verify, 2: Setup
    const [email, setEmail] = useState('');
    const [tempToken, setTempToken] = useState<string | null>(null);

    // Setup Data
    const [qrData, setQrData] = useState<{ qr_image: string, secret: string } | null>(null);

    // Clear stale tokens
    useEffect(() => {
        authService.clearTokens();
    }, []);

    // 1. Login Mutation
    const loginMutation = useMutation({
        mutationFn: (data: LoginCredentials) => authService.login(data),
        onSuccess: (response) => {
            console.log('Login Success:', response);
            if (response.temp_token) {
                setTempToken(response.temp_token);

                if (response.mfa_setup_required || response.status === 'mfa_setup_required') {
                    message.info('MFA setup required. Loading QR Code...');
                    setCurrentStep(2); // Go to Setup
                    setupMfaMutation.mutate(response.temp_token);
                } else if (response.mfa_required || response.status === 'mfa_required') {
                    message.success('Enter MFA code');
                    setCurrentStep(1); // Go to Verify
                } else {
                    message.error('Unexpected login state: ' + (response.status || 'unknown'));
                }
            } else {
                message.error('No session token received');
            }
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Login failed');
        },
    });

    // 2. Setup MFA Mutation (Fetch QR)
    const setupMfaMutation = useMutation({
        mutationFn: (token: string) => authService.setupMFA(token),
        onSuccess: (data) => {
            setQrData(data);
        },
        onError: () => {
            message.error('Failed to load MFA setup');
        }
    });

    // 3. Verify Mutation (Handles both Setup-Verify and Login-Verify)
    const verifyMutation = useMutation({
        mutationFn: (code: string) => {
            if (!tempToken) throw new Error("Session expired");

            // If in Step 2 (Setup), use verifySetup
            if (currentStep === 2) {
                return authService.verifySetup({ temp_token: tempToken, code });
            }
            // If in Step 1 (Verify), use verifyMFA
            return authService.verifyMFA({ temp_token: tempToken, code });
        },
        onSuccess: async (response: any) => {
            // BACKEND RESPONSE FORMAT: { token: "...", admin: { ... }, type: "bearer", expires_in: ... }
            // FRONTEND EXPECTATION (old): { tokens: { access_token: ... }, user: ... }

            // Normalize the response
            const accessToken = response.token || response.tokens?.access_token;
            const user = response.admin || response.user;
            // Backend might not send refresh token in this specific response based on AdminAuthService.php code
            // But we need to check if it's there or use the access token temporarily if that's the current design
            const refreshToken = response.refresh_token || response.tokens?.refresh_token || 'dummy_refresh_token';

            if (accessToken) {
                authService.storeTokens(accessToken, refreshToken);

                // Fetch the fully mapped profile using loadUser
                await useAuthStore.getState().loadUser();

                message.success('Login successful!');
                navigate('/dashboard');
            } else {
                message.error('Invalid response from server: Missing token');
            }
        },
        onError: (error: any) => {
            const errorData = error.response?.data;
            const errorMessage = errorData?.message || 'Verification failed';

            // Check for specific session expiry/invalid errors
            // Laravel ValidationException puts field errors in 'errors' object
            const tokenError = errorData?.errors?.token?.[0];

            if (tokenError && (tokenError.includes('expired') || tokenError.includes('invalid'))) {
                message.error(tokenError);
                // Reset to login step
                setTempToken(null);
                setCurrentStep(0);
                return;
            }

            message.error(errorMessage);
        }
    });

    const handleLogin = (values: LoginCredentials) => {
        setEmail(values.email);
        loginMutation.mutate(values);
    };

    const handleVerifyLike = (values: { otp: string }) => {
        verifyMutation.mutate(values.otp);
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', // Darker theme for Admin
        }}>
            <Card
                style={{
                    width: 480,
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    borderRadius: 16
                }}
            >
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <Image
                        width={60}
                        src="/logo.png"
                        fallback="https://placehold.co/60x60?text=BID"
                        preview={false}
                        style={{ marginBottom: 16 }}
                    />
                    <Title level={3} style={{ marginTop: 0 }}>Admin Portal</Title>
                    <Paragraph type="secondary">Secure Access Gateway</Paragraph>

                    <Steps current={currentStep > 1 ? 1 : currentStep} size="small" style={{ marginBottom: 24 }}>
                        <Step title="Login" icon={<UserOutlined />} />
                        <Step title="MFA" icon={<SafetyOutlined />} />
                    </Steps>
                </div>

                {/* STEP 0: LOGIN */}
                {currentStep === 0 && (
                    <Form onFinish={handleLogin} layout="vertical" size="large">
                        <Form.Item name="email" rules={[{ required: true, type: 'email' }]}>
                            <Input prefix={<UserOutlined />} placeholder="Email" />
                        </Form.Item>
                        <Form.Item name="password" rules={[{ required: true }]}>
                            <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loginMutation.isPending}>
                            Sign In
                        </Button>
                    </Form>
                )}

                {/* STEP 1: VERIFY MFA */}
                {currentStep === 1 && (
                    <Form onFinish={handleVerifyLike} layout="vertical" size="large">
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            <Paragraph>Enter the code from your authenticator app.</Paragraph>
                        </div>
                        <Form.Item name="otp" rules={[{ required: true, len: 6 }]}>
                            <Input
                                prefix={<SafetyOutlined />}
                                placeholder="000 000"
                                maxLength={6}
                                style={{ textAlign: 'center', letterSpacing: 4, fontSize: 18 }}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" block loading={verifyMutation.isPending}>
                            Verify Access
                        </Button>
                        <Button type="link" block onClick={() => { setCurrentStep(0); setTempToken(null); }}>
                            Back to Login
                        </Button>
                    </Form>
                )}

                {/* STEP 2: SETUP MFA */}
                {currentStep === 2 && (
                    <div style={{ textAlign: 'center' }}>
                        <Title level={4}>Secure Your Account</Title>
                        <Paragraph>Scan this QR code with Google Authenticator</Paragraph>

                        {setupMfaMutation.isPending ? (
                            <Spin size="large" />
                        ) : qrData ? (
                            <>
                                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, display: 'inline-block', marginBottom: 16 }}>
                                    <img src={`data:image/svg+xml;base64,${qrData.qr_image}`} alt="MFA QR" width={200} />
                                </div>
                                <Paragraph type="secondary" copyable>{qrData.secret}</Paragraph>

                                <Form onFinish={handleVerifyLike} layout="vertical" size="large">
                                    <Form.Item name="otp" rules={[{ required: true, len: 6, message: 'Enter the code to confirm setup' }]}>
                                        <Input
                                            placeholder="Enter 6-digit code to confirm"
                                            maxLength={6}
                                            style={{ textAlign: 'center', letterSpacing: 4 }}
                                        />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit" block loading={verifyMutation.isPending}>
                                        Confirm & Login
                                    </Button>
                                </Form>
                            </>
                        ) : (
                            <Paragraph type="danger">Failed to load QR Code</Paragraph>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default LoginPage;
